import {
  billableFields,
  billingTypesEnum,
  BUSINESS_TYPES,
  CONSUMER_INSIGHTS,
  CREATIVE,
  MEDIA,
  OTHER,
} from '../constants';

/*  eslint no-param-reassign: "error"   */

export const getBillableFields = derivedObj => {
  const billableValues = {};
  // For quotes, when quote line is removed the value needs to be reset to 0. For campaigns
  // since it is calculated based on change requests it just gets the summed values and provides 0 for the other non applicable fields.
  billableFields.forEach(item => {
    billableValues[item] = derivedObj[item] || 0;
  });
  return billableValues;
};

export const calculateBillableFieldValues = (obj, billingType) => {
  if (
    billingType === billingTypesEnum.EXTERNAL ||
    billingType === billingTypesEnum.WKZ
  ) {
    obj.billableBudgetMediaExternal = obj.netClientBudgetExternalMedia;
    obj.billableBudgetMediaWKZ = obj.wkzBudgetMedia;
    obj.billableBudgetMediaInternal = obj.coInvestMedia;
    obj.billableBudgetCreativeExternal = obj.netClientBudgetExternalCreative;
    obj.billableBudgetCreativeWKZ = obj.wkzBudgetCreative;
    obj.billableBudgetCreativeInternal = obj.coInvestCreative;
    obj.billableBudgetCIExternal = obj.netClientBudgetExternalCI;
    obj.billableBudgetCIWKZ = obj.wkzBudgetCI;
    obj.billableBudgetCIInternal = obj.coInvestCI;
    obj.billableBudgetOtherExternal = obj.netClientBudgetExternalOther;
    obj.billableBudgetOtherWKZ = obj.wkzBudgetOther;
    obj.billableBudgetOtherInternal = obj.coInvestOther;
  }

  if (billingType === billingTypesEnum.INTERNAL) {
    obj.billableBudgetMediaInternal =
      obj.coInvestMedia + obj.netClientBudgetExternalMedia;
    obj.billableBudgetMediaWKZ = obj.wkzBudgetMedia;
    obj.billableBudgetCreativeInternal =
      obj.coInvestCreative + obj.netClientBudgetExternalCreative;
    obj.billableBudgetCreativeWKZ = obj.wkzBudgetCreative;
    obj.billableBudgetCIInternal =
      obj.coInvestCI + obj.netClientBudgetExternalCI;
    obj.billableBudgetCIWKZ = obj.wkzBudgetCI;
    obj.billableBudgetOtherInternal =
      obj.coInvestOther + obj.netClientBudgetExternalOther;
    obj.billableBudgetOtherWKZ = obj.wkzBudgetOther;
  }
  return obj;
};

export const getBusinessType = (dataLine, products = []) => {
  const isQuote = Object.keys(dataLine).includes('quoteId');
  const isCampaign = Object.keys(dataLine).includes('campaignId');
  const isPatchOperation = Object.keys(dataLine).includes('newValues');
  if (isQuote) {
    const { productId } = dataLine;
    const { business } = products[productId];
    const businessValue = BUSINESS_TYPES.find(item => {
      if (business.includes(item)) return item;
      return null;
    });
    return businessValue;
  }
  if (isCampaign || isPatchOperation) {
    const { business } = dataLine.product;
    const businessValue = BUSINESS_TYPES.find(item => {
      if (business.includes(item)) return item;
      return null;
    });
    return businessValue;
  }
  return null;
};

export const calculateBillableSums = (dataLines, products = []) => {
  const isPatchOperation =
    dataLines.length > 0
      ? Object.keys(dataLines[0]).includes('newValues')
      : false;
  //  Variables added to keep the value of different business types in each loop
  let coInvestMediaValue = 0;
  let netClientBudgetExternalMediaValue = 0;
  let wkzBudgetMediaValue = 0;
  let coInvestCreativeValue = 0;
  let netClientBudgetExternalCreativeValue = 0;
  let wkzBudgetCreativeValue = 0;
  let coInvestCIValue = 0;
  let netClientBudgetExternalCIValue = 0;
  let wkzBudgetCIValue = 0;
  let coInvestOtherValue = 0;
  let netClientBudgetExternalOtherValue = 0;
  let wkzBudgetOtherValue = 0;
  return dataLines.reduce(
    (
      {
        coInvestMedia,
        coInvestCreative,
        coInvestCI,
        coInvestOther,
        netClientBudgetExternalMedia,
        wkzBudgetMedia,
        netClientBudgetExternalCreative,
        wkzBudgetCreative,
        netClientBudgetExternalCI,
        wkzBudgetCI,
        netClientBudgetExternalOther,
        wkzBudgetOther,
      },
      dataLine,
    ) => {
      const businessValue = getBusinessType(dataLine, products);
      const obj = isPatchOperation ? dataLine.newValues : dataLine;
      if (businessValue === MEDIA) {
        coInvestMediaValue = coInvestMedia + obj.coInvest;
        netClientBudgetExternalMediaValue =
          netClientBudgetExternalMedia + obj.netClientBudgetExternal;
        wkzBudgetMediaValue = wkzBudgetMedia + obj.wkzBudget;
      }
      if (businessValue === CREATIVE) {
        coInvestCreativeValue = coInvestCreative + obj.coInvest;
        netClientBudgetExternalCreativeValue =
          netClientBudgetExternalCreative + obj.netClientBudgetExternal;
        wkzBudgetCreativeValue = wkzBudgetCreative + obj.wkzBudget;
      }
      if (businessValue === CONSUMER_INSIGHTS) {
        coInvestCIValue = coInvestCI + obj.coInvest;
        netClientBudgetExternalCIValue =
          netClientBudgetExternalCI + obj.netClientBudgetExternal;
        wkzBudgetCIValue = wkzBudgetCI + obj.wkzBudget;
      }
      if (businessValue === OTHER) {
        coInvestOtherValue = coInvestOther + obj.coInvest;
        netClientBudgetExternalOtherValue =
          netClientBudgetExternalOther + obj.netClientBudgetExternal;
        wkzBudgetOtherValue = wkzBudgetOther + obj.wkzBudget;
      }
      return {
        coInvestMedia: coInvestMediaValue,
        netClientBudgetExternalMedia: netClientBudgetExternalMediaValue,
        wkzBudgetMedia: wkzBudgetMediaValue,
        coInvestCreative: coInvestCreativeValue,
        netClientBudgetExternalCreative: netClientBudgetExternalCreativeValue,
        wkzBudgetCreative: wkzBudgetCreativeValue,
        coInvestCI: coInvestCIValue,
        netClientBudgetExternalCI: netClientBudgetExternalCIValue,
        wkzBudgetCI: wkzBudgetCIValue,
        coInvestOther: coInvestOtherValue,
        netClientBudgetExternalOther: netClientBudgetExternalOtherValue,
        wkzBudgetOther: wkzBudgetOtherValue,
      };
    },
    {
      coInvestMedia: 0,
      coInvestCreative: 0,
      coInvestCI: 0,
      coInvestOther: 0,
      netClientBudgetExternalMedia: 0,
      wkzBudgetMedia: 0,
      netClientBudgetExternalCreative: 0,
      wkzBudgetCreative: 0,
      netClientBudgetExternalCI: 0,
      wkzBudgetCI: 0,
      netClientBudgetExternalOther: 0,
      wkzBudgetOther: 0,
    },
  );
};

export function calculateBillableBudget({
  productsById,
  opportunity,
  quote,
  quoteLinesById,
  campaign,
  campaignLinesById,
  campaignLines,
}) {
  if (quote) {
    const { billingType } = opportunity;
    const { quoteLines: quoteLineIds } = quote;
    const quoteLines = quoteLineIds.map(id => quoteLinesById[id]);

    const sums = calculateBillableSums(quoteLines, productsById);
    const billableFieldValues = calculateBillableFieldValues(sums, billingType);
    return getBillableFields(billableFieldValues);
  }

  if (campaign) {
    const { billingType } = campaign.opportunity;
    const { campaignLineIds } = campaign;
    const campaignLinesRows = !campaignLines
      ? campaignLineIds.map(id => campaignLinesById[id])
      : [];

    const sums = campaignLines
      ? calculateBillableSums(campaignLines)
      : calculateBillableSums(campaignLinesRows);
    const billableFieldValues = calculateBillableFieldValues(sums, billingType);
    return getBillableFields(billableFieldValues);
  }
  return null;
}
